import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mapApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { MapTemplate } from '../../../types/adminTypes';

type MapSummary = Omit<MapTemplate, 'cells'>;

export function MapsPage() {
  const [maps,    setMaps]    = useState<MapSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const load = async () => {
    try {
      setLoading(true);
      setMaps(await mapApi.list() as MapSummary[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`${t('monsters.delete.confirm')} "${name}"?`)) return;
    try {
      await mapApi.delete(id);
      setMaps((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      alert(`${t('common.error')} ${(e as Error).message}`);
    }
  };

  return (
    <>
      <div className="adminHeader">
        <h1>{t('maps.title')}</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btnSecondary" onClick={() => navigate('/admin/maps/hub')} style={{ background: '#3a5a3a', color: '#e8c87a' }}>
            🏰 {t('admin.nav.hub' as any)}
          </button>
          <button className="btn btnPrimary" onClick={() => navigate('/admin/maps/new')}>
            {t('maps.new')}
          </button>
        </div>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <div className="adminCard">
          <div className="adminCardHeader">
            <span>{t('maps.listHeader')} ({maps.length})</span>
          </div>

          {loading ? (
            <div className="spinner" />
          ) : maps.length === 0 ? (
            <div className="emptyState">
              {t('maps.empty')}{' '}
              <Link to="/admin/maps/new" style={{ color: 'var(--accent)' }}>{t('maps.createFirst')}</Link>
            </div>
          ) : (
            <table className="adminTable">
              <thead>
                <tr>
                  <th>{t('maps.col.name')}</th>
                  <th>{t('maps.col.dimensions')}</th>
                  <th>{t('maps.col.cells')}</th>
                  <th>{t('maps.col.description')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {maps.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 600 }}>{m.name}</td>
                    <td>
                      <span className="badge">{m.width}×{m.height}</span>
                    </td>
                    <td style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                      {m.width * m.height} {t('maps.cellsUnit')}
                    </td>
                    <td style={{ color: 'var(--text-dim)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.description || '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btnGhost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => navigate(`/admin/maps/${m.id!}/edit`)}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          className="btn btnDanger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => void handleDelete(m.id!, m.name)}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
